// ── Zone visit analysis ───────────────────────────────────────────────────
// Hybrid: exact visits for rule-backed zones, a Trip-stop estimate for the rest.
// Nothing is ever written to Geotab; pre-existing rules are only ever read.
//
// EXACT PATH (zone.ruleIds.length > 0): reads ExceptionEvent for the rule(s)
// that already target that zone. This is the literal same data Geotab's own
// "Zone Visit" reports are built from — every row of a real "Advanced Zone
// Visit Details" export is one ExceptionEvent of that zone's ZoneStop rule
// (auto-generated by Geotab when the zone's MustIdentifyStops flag is on;
// discovered via RuleSearch baseType 'ZoneStop', see zone-activity-data.ts).
// Reading the same events makes this match exactly, by construction.
//
// ESTIMATE PATH (zones with no rule): one visit per Trip whose stop location
// lands inside the zone's polygon — entry is the trip's stop time, exit is
// the next trip's start. NO merging of near-adjacent stops: a real export
// showed two visits 7 seconds and 0.000 miles apart as separate rows, so a
// ZoneStop event spans exactly one vehicle stop, and so does an estimated
// visit here. Drive-throughs are correctly invisible to this model — Geotab
// "does not display instances when a vehicle passes through a zone without
// stopping" (support.geotab.com/help/mygeotab/reports/productivity-reports/
// zone-visits-report) — and a Trip's own stop already encodes Geotab's
// documented stop definition (ignition off, or under 1 km/h for 200+
// seconds), so there is no separate threshold to apply. Estimated visits can
// still differ from a ZoneStop rule's events at the polygon edge (the rule
// evaluates GPS inside the boundary; this path evaluates the stop point),
// which is why they are labelled estimated rather than verified.

import { callGeotabAPI } from '../api/geotab-client';
import {
  fetchAdaptiveWindow, fetchRangeChunked, mapLimit, splitDateRange, withRetry, MAX_CONCURRENT,
  FLEET_WIDE_TRIP_LIMIT,
} from '../api/fetch-range';
import { state } from '../state';
import { hourInSessionTz } from '../utils/timezone';
import type { ExceptionEvent, Trip } from '../api/types';
import { loadZoneActivityPageData } from './zone-activity-data';
import { pointInZone, zonesAtPoint } from './zone-activity-geometry';
import type { ZoneGrid } from './zone-activity-geometry';
import type { PreparedZone, VehicleZoneAgg, ZoneAgg, ZoneAnalysisResult, ZoneVisit } from './zone-activity-types';

// Trip.Get, called fleet-wide (no deviceSearch) as this file always does, silently caps at
// FLEET_WIDE_TRIP_LIMIT regardless of a higher resultsLimit — see its definition for how that
// was confirmed. EVENT_LIMIT is a different entity (ExceptionEvent) and keeps its own value.
const TRIP_LIMIT  = FLEET_WIDE_TRIP_LIMIT;
const EVENT_LIMIT = 50000;
const WINDOW_DAYS = 7;

/**
 * How far back to fetch Trips beyond the analysis range, so a visit already
 * in progress when the range opens resolves to its TRUE start time (matching
 * Geotab's own report, which shows a visit's real arrival even when it's
 * before the report's date range — not clamped to the range boundary).
 * Trips are low-volume compared to raw GPS, so this lookback is cheap
 * regardless of fleet size — the fetch is already fleet-wide either way.
 *
 * KNOWN LIMIT of the estimate path: a stay can only open from a fetched
 * trip's stop, so a vehicle that arrived and then sat longer than this
 * lookback (a trailer dropped in a yard weeks ago) has its arrival trip
 * outside the fetch window and produces NO estimated visit at all — it is
 * absent rather than reported with an approximate entry. Rule-backed zones
 * are unaffected: their still-open ExceptionEvent carries the true arrival
 * however old it is. Raising this constant widens the recoverable window at
 * proportional fetch cost.
 */
const TRIP_LOOKBACK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Maximum analysable range by number of vehicles in scope. Group-scoped
 * analysis cost tracks the date range (one fleet-wide Trip fetch), not
 * device count, so this is a conservative holdover from an earlier
 * GPS-based design — safe to keep, but likely loosenable in a follow-up now
 * that no per-device GPS fetch exists.
 */
export const VEHICLE_DAY_TIERS: Array<[maxVehicles: number, maxDays: number]> = [
  [120,  90],
  [350,  31],
  [800,  14],
  [1600,  7],
];
const MIN_TIER_DAYS = 3;

export function maxRangeDaysFor(vehicleCount: number): number {
  for (const [maxVehicles, maxDays] of VEHICLE_DAY_TIERS) {
    if (vehicleCount <= maxVehicles) return maxDays;
  }
  return MIN_TIER_DAYS;
}

/**
 * Looking up one vehicle or driver touches a small, range-bounded slice of
 * data regardless of fleet size, so it doesn't need the fleet-wide adaptive
 * cap — a flat ceiling is enough. 180 days doubles Geotab's own 6-month
 * reprocessing window, which is as long a lookback as this class of question
 * ("what has this truck/driver been doing") realistically needs.
 */
export const SINGLE_ENTITY_MAX_DAYS = 180;

/** A driver rarely rotates through more than a handful of vehicles; this is a
 *  safety backstop, not an expected limit, mirroring heatmap-data.ts's
 *  MAX_DEVICES pattern (cap + keep the most relevant by activity). */
const DRIVER_DEVICE_SAFETY_CAP = 60;

export interface DriverDeviceDiscovery {
  deviceIds: string[];
  truncated: boolean;
}

/**
 * Finds every device a driver actually drove during the range, so a "by
 * driver" analysis can scope itself to just those vehicles. Trips are
 * fetched fleet-wide (the same unscoped call runZoneAnalysis's own Phase A
 * makes — cost tracks the date range, not fleet size) and filtered by
 * `trip.driver.id` here instead of by device-group membership.
 */
export async function discoverDriverDevices(
  fromISO: string,
  toISO: string,
  driverId: string,
  onProgress: (msg: string) => void,
): Promise<DriverDeviceDiscovery> {
  const windows = splitDateRange(fromISO, toISO, WINDOW_DAYS);
  const tripCounts = new Map<string, number>();
  let windowsDone = 0;

  await mapLimit(windows, MAX_CONCURRENT, async ([wFrom, wTo]) => {
    let rows: Trip[];
    try {
      rows = await fetchAdaptiveWindow<Trip>(
        wFrom, wTo, TRIP_LIMIT,
        (f, t) => withRetry(() => callGeotabAPI<Trip[]>('Get', {
          typeName: 'Trip',
          search: { fromDate: f, toDate: t },
          resultsLimit: TRIP_LIMIT,
        })),
        {},
      );
    } catch {
      return; // this window's trips are unavailable; discovery continues with what it has
    }
    for (const trip of rows) {
      if (trip?.driver?.id !== driverId) continue;
      const devId = trip.device?.id;
      if (!devId) continue;
      tripCounts.set(devId, (tripCounts.get(devId) ?? 0) + 1);
    }
    windowsDone++;
    onProgress(`Finding this driver's vehicles (${windowsDone} of ${windows.length} weeks)…`);
  });

  // Most-driven first, so a truncation (rare) drops the least-relevant vehicles.
  const ranked = [...tripCounts.entries()].sort((a, b) => b[1] - a[1]).map(([id]) => id);
  const truncated = ranked.length > DRIVER_DEVICE_SAFETY_CAP;
  return { deviceIds: truncated ? ranked.slice(0, DRIVER_DEVICE_SAFETY_CAP) : ranked, truncated };
}

export interface ZoneAnalysisParams {
  fromISO: string;
  toISO: string;
  deviceIds: string[];
  onProgress: (msg: string) => void;
}

/** Per-device trip index, as parallel arrays sorted by start time. */
interface DeviceTrips {
  starts: number[];
  /** Each trip's stop (ignition-off / arrival) time — NaN if the trip hasn't stopped yet. */
  stops: number[];
  /** Where each trip stopped — parallel to `stops`. NaN when unknown. */
  stopLat: number[];
  stopLng: number[];
  drivers: Array<string | null>;
}

interface OpenZoneStay {
  entryMs: number;
  driverId: string | null;
}

/**
 * Walks one device's trips in order, treating each trip's stop location as
 * ground truth for "where did it stop". One trip-stop inside a zone = exactly
 * one visit: entry is the trip's stop time, exit is the next trip's start.
 * Every open visit closes the moment the next trip starts moving — even when
 * that next trip stops in the SAME zone seconds later, which begins a new,
 * separate visit. This mirrors a ZoneStop rule's own event boundaries: a real
 * Geotab export showed two rows 7 seconds / 0.000 miles apart as two visits.
 *
 * Visits that closed entirely before `fromMs` are dropped at the end — the
 * lookback-extended walk only produced them to resolve the true entry time of
 * a visit that extends into the requested range. A stop AFTER `toMs` never
 * opens a stay at all: Geotab's Trip search returns any trip overlapping the
 * window, so the last trip fetched routinely stops past the range end, and
 * counting that as a visit would report arrivals that happened after the
 * period the user asked about.
 *
 * `exactZoneIdx` — indices already covered by a real Rule (see
 * computeExactVisits below) are treated as if they don't exist here: a stop
 * landing in one is simply invisible to this walk, so the same visit can
 * never be reported twice from two different sources.
 */
function computeDeviceVisits(
  deviceId: string,
  trips: DeviceTrips | undefined,
  zones: PreparedZone[],
  grid: ZoneGrid,
  fromMs: number,
  toMs: number,
  exactZoneIdx: Set<number>,
): ZoneVisit[] {
  if (!trips || !trips.starts.length) return [];

  const visits: ZoneVisit[] = [];
  const open = new Map<number, OpenZoneStay>();
  // An open stay's dwell runs to the range end — but never past NOW, or every
  // vehicle currently parked in a zone would bank hours it hasn't spent yet
  // (the range end is an end-of-day timestamp, so "today" is in the future).
  const openDwellEndMs = Math.min(toMs, Date.now());

  const closeZone = (idx: number, exitMs: number | null): void => {
    const o = open.get(idx);
    if (!o) return;
    open.delete(idx);
    // Trips are sequential in practice, so exit < entry means anomalous data
    // (overlapping/reprocessed trips). Drop it rather than render a row whose
    // Exit reads earlier than its Entry.
    if (exitMs !== null && exitMs < o.entryMs) return;
    visits.push({
      deviceId, zoneId: zones[idx].id,
      entryMs: o.entryMs, exitMs,
      dwellMs: Math.max(0, (exitMs ?? openDwellEndMs) - o.entryMs),
      alreadyInsideAtStart: o.entryMs < fromMs,
      stillInsideAtEnd: exitMs === null,
      driverId: o.driverId,
    });
  };

  for (let i = 0; i < trips.starts.length; i++) {
    // The vehicle started moving at starts[i] — every stay open from the
    // previous stop ends here, unconditionally.
    for (const idx of [...open.keys()]) closeZone(idx, trips.starts[i]);

    const stopMs = trips.stops[i];
    const lat = trips.stopLat[i];
    const lng = trips.stopLng[i];
    const hasStop =
      Number.isFinite(stopMs) && Number.isFinite(lat) && Number.isFinite(lng) &&
      !(lat === 0 && lng === 0);
    if (!hasStop || stopMs > toMs) continue;

    for (const idx of zonesAtPoint(grid, lat, lng)) {
      if (exactZoneIdx.has(idx)) continue;
      if (!pointInZone(lat, lng, zones[idx])) continue;
      open.set(idx, { entryMs: stopMs, driverId: trips.drivers[i] ?? null });
    }
  }
  for (const idx of [...open.keys()]) closeZone(idx, null);

  return visits.filter(v => v.exitMs === null || v.exitMs > fromMs);
}

/**
 * One ExceptionEvent IS one visit — read directly, no computation, no merge
 * threshold. Uses the requested range as-is, with no lookback: a real export
 * showed a visit's `ExceptionDetailStartTime` before the report's own
 * `FromDate`, proving Geotab's ExceptionEvent search already returns an
 * event's true, un-clipped activeFrom/activeTo whenever the event merely
 * overlaps the searched range — so, unlike the Trip-stop estimate below,
 * there's no "seed" problem here to solve with a backward-extended fetch.
 */
async function computeExactVisits(
  ruleToZoneId: Map<string, string>,
  scope: Set<string>,
  fromISO: string,
  toISO: string,
  fromMs: number,
  toMs: number,
  onProgress: (msg: string) => void,
  onCapHit: () => void,
): Promise<{ visits: ZoneVisit[]; eventWindowFailures: number }> {
  const visits: ZoneVisit[] = [];
  let eventWindowFailures = 0;
  const ruleIds = [...ruleToZoneId.keys()];
  if (!ruleIds.length) return { visits, eventWindowFailures };

  onProgress('Fetching verified zone visits…');
  let rulesDone = 0;

  await mapLimit(ruleIds, MAX_CONCURRENT, async (ruleId) => {
    const zoneId = ruleToZoneId.get(ruleId)!;
    let events: ExceptionEvent[];
    try {
      events = await fetchRangeChunked<ExceptionEvent>({
        fromISO, toISO, perCallLimit: EVENT_LIMIT,
        fetch: (f, t) => withRetry(() => callGeotabAPI<ExceptionEvent[]>('Get', {
          typeName: 'ExceptionEvent',
          search: { ruleSearch: { id: ruleId }, fromDate: f, toDate: t },
          resultsLimit: EVENT_LIMIT,
        })),
        onCap: onCapHit,
      });
    } catch {
      eventWindowFailures++;
      return;
    }

    // A still-open exception's activeTo is a far-future sentinel, not a real
    // departure — anything claiming to end in the future is "still inside".
    // A REAL departure after the range end stays as-is: Geotab's own report
    // shows the true departure even past its ToDate, so clipping it here
    // would create a visible mismatch against the report.
    const nowMs = Date.now();
    // Dwell for a still-open visit runs to the range end but never past now —
    // the range end is an end-of-day timestamp, so a range ending today would
    // otherwise credit hours that haven't elapsed.
    const openDwellEndMs = Math.min(toMs, nowMs);

    for (const ev of events) {
      const devId = ev.device?.id;
      if (!devId || !scope.has(devId)) continue;
      const entryMs = ev.activeFrom ? new Date(ev.activeFrom).getTime() : NaN;
      if (!Number.isFinite(entryMs)) continue;
      const rawExitMs = ev.activeTo ? new Date(ev.activeTo).getTime() : NaN;
      const exitMs = (Number.isFinite(rawExitMs) && rawExitMs <= nowMs) ? rawExitMs : null;
      if (exitMs !== null && exitMs <= fromMs) continue; // fully closed before the range opened

      const drvId = ev.driver?.id;
      visits.push({
        deviceId: devId, zoneId,
        entryMs, exitMs,
        dwellMs: Math.max(0, (exitMs ?? openDwellEndMs) - entryMs),
        alreadyInsideAtStart: entryMs < fromMs,
        stillInsideAtEnd: exitMs === null || exitMs > toMs,
        driverId: drvId && drvId !== 'UnknownDriverId' ? drvId : null,
      });
    }
    rulesDone++;
    onProgress(`Fetching verified zone visits (${rulesDone} of ${ruleIds.length} zones)…`);
  });

  return { visits, eventWindowFailures };
}

export async function runZoneAnalysis(params: ZoneAnalysisParams): Promise<ZoneAnalysisResult> {
  const { fromISO, toISO, deviceIds, onProgress } = params;
  const data   = await loadZoneActivityPageData();
  const fromMs = new Date(fromISO).getTime();
  const toMs   = new Date(toISO).getTime();
  const epoch  = state.sessionEpoch;

  const scope = new Set(deviceIds);

  // ── Zone ↔ rule mapping, from the already-loaded, already-cached zones ──
  const exactZoneIdx = new Set<number>();
  const ruleToZoneId = new Map<string, string>();
  data.grid.zones.forEach((z, idx) => {
    if (!z.ruleIds.length) return;
    exactZoneIdx.add(idx);
    z.ruleIds.forEach(rid => ruleToZoneId.set(rid, z.id));
  });

  let capHit = false;
  let tripWindowFailures = 0;

  // ── Exact visits — independent of, and fetched ahead of, the trip walk ──
  const { visits: exactVisits, eventWindowFailures } = await computeExactVisits(
    ruleToZoneId, scope, fromISO, toISO, fromMs, toMs, onProgress, () => { capHit = true; },
  );
  if (epoch !== state.sessionEpoch) throw new Error('Session ended');

  // Extended backward so a visit already in progress at fromMs resolves to
  // its true start — see TRIP_LOOKBACK_MS.
  const fetchFromISO = new Date(fromMs - TRIP_LOOKBACK_MS).toISOString();
  const windows = splitDateRange(fetchFromISO, toISO, WINDOW_DAYS);

  // ── Fleet-wide trips — the estimate path, for zones with no rule ────────
  onProgress('Fetching trips…');
  const tripsByDevice = new Map<string, DeviceTrips>();
  const seenTrips = new Set<string>();
  let windowsDone = 0;

  await mapLimit(windows, MAX_CONCURRENT, async ([wFrom, wTo]) => {
    // A failed window degrades to "skip it" rather than throwing: `rows`
    // going missing would otherwise reject mapLimit's Promise.all and abort
    // the WHOLE analysis over one transient/rate-limit error, instead of the
    // far smaller cost of understating trip coverage for a handful of weeks.
    let rows: Trip[];
    try {
      rows = await fetchAdaptiveWindow<Trip>(
        wFrom, wTo, TRIP_LIMIT,
        (f, t) => withRetry(() => callGeotabAPI<Trip[]>('Get', {
          typeName: 'Trip',
          search: { fromDate: f, toDate: t },
          resultsLimit: TRIP_LIMIT,
        })),
        { onCap: () => { capHit = true; } },
      );
    } catch {
      tripWindowFailures++;
      capHit = true;
      return;
    }

    for (const trip of rows) {
      const devId = trip?.device?.id;
      if (!devId || !scope.has(devId)) continue;
      if (trip.id) {
        if (seenTrips.has(trip.id)) continue;
        seenTrips.add(trip.id);
      }
      const startMs = trip.start ? new Date(trip.start).getTime() : NaN;
      if (!Number.isFinite(startMs)) continue;
      const stopMs = trip.stop ? new Date(trip.stop).getTime() : NaN;

      let entry = tripsByDevice.get(devId);
      if (!entry) {
        entry = { starts: [], stops: [], stopLat: [], stopLng: [], drivers: [] };
        tripsByDevice.set(devId, entry);
      }
      entry.starts.push(startMs);
      entry.stops.push(stopMs);
      const stopPt = trip.stopPoint;
      entry.stopLat.push(stopPt && typeof stopPt.y === 'number' ? stopPt.y : NaN);
      entry.stopLng.push(stopPt && typeof stopPt.x === 'number' ? stopPt.x : NaN);
      const drvId = trip.driver?.id;
      entry.drivers.push(drvId && drvId !== 'UnknownDriverId' ? drvId : null);
    }
    windowsDone++;
    onProgress(`Fetching trips (${windowsDone} of ${windows.length} weeks)…`);
    // `rows` falls out of scope here — memory stays flat regardless of range.
  });

  if (epoch !== state.sessionEpoch) throw new Error('Session ended');

  // Sort each device's trips by start — computeDeviceVisits walks them in order.
  tripsByDevice.forEach(t => {
    const order = t.starts.map((s, i) => [s, i] as const).sort((a, b) => a[0] - b[0]);
    t.starts   = order.map(([s]) => s);
    t.stops    = order.map(([, i]) => t.stops[i]);
    t.stopLat  = order.map(([, i]) => t.stopLat[i]);
    t.stopLng  = order.map(([, i]) => t.stopLng[i]);
    t.drivers  = order.map(([, i]) => t.drivers[i]);
  });

  // ── Per device: derive visits from the trip chain — pure computation, no
  // further API calls, so this is a plain loop (not mapLimit). A periodic
  // yield keeps the tab responsive on a very large fleet. ──────────────────
  const allVisits: ZoneVisit[] = [...exactVisits];
  let devicesDone = 0;

  for (const deviceId of deviceIds) {
    if (epoch !== state.sessionEpoch) throw new Error('Session ended');

    const visits = computeDeviceVisits(deviceId, tripsByDevice.get(deviceId), data.grid.zones, data.grid, fromMs, toMs, exactZoneIdx);
    if (visits.length) allVisits.push(...visits);

    devicesDone++;
    if (devicesDone % 25 === 0 || devicesDone === deviceIds.length) {
      onProgress(`Analysing zone visits (${devicesDone} of ${deviceIds.length} vehicles)…`);
    }
    if (devicesDone % 200 === 0) await new Promise(r => setTimeout(r, 0));
  }

  // ── Aggregate ───────────────────────────────────────────────────────────
  const byZone = new Map<string, ZoneAgg>();
  for (const v of allVisits) {
    let agg = byZone.get(v.zoneId);
    if (!agg) {
      agg = {
        zoneId: v.zoneId,
        visitCount: 0,
        totalDwellMs: 0, stillInsideCount: 0,
        byVehicle: new Map<string, VehicleZoneAgg>(),
        byHour: new Array(24).fill(0),
      };
      byZone.set(v.zoneId, agg);
    }
    agg.visitCount++;
    agg.totalDwellMs += v.dwellMs;
    if (v.stillInsideAtEnd) agg.stillInsideCount++;
    agg.byHour[hourInSessionTz(v.entryMs)]++;

    let veh = agg.byVehicle.get(v.deviceId);
    if (!veh) {
      veh = {
        deviceId: v.deviceId, visits: [], totalDwellMs: 0,
        firstEntryMs: v.entryMs, lastExitMs: null,
        stillInside: false, driverIds: new Set<string>(),
      };
      agg.byVehicle.set(v.deviceId, veh);
    }
    veh.visits.push(v);
    veh.totalDwellMs += v.dwellMs;
    if (v.entryMs < veh.firstEntryMs) veh.firstEntryMs = v.entryMs;
    if (v.exitMs !== null && (veh.lastExitMs === null || v.exitMs > veh.lastExitMs)) {
      veh.lastExitMs = v.exitMs;
    }
    if (v.stillInsideAtEnd) veh.stillInside = true;
    if (v.driverId) veh.driverIds.add(v.driverId);
  }

  byZone.forEach(agg => agg.byVehicle.forEach(v => v.visits.sort((a, b) => a.entryMs - b.entryMs)));

  return {
    fromISO, toISO, fromMs, toMs,
    scopedDeviceCount: deviceIds.length,
    ranAtMs: Date.now(),
    byZone,
    capHit,
    tripWindowFailures,
    eventWindowFailures,
  };
}
